import {
	createLog,
	type DeliveryServiceConfig,
	type DeliveryServiceConfigDtoInput,
	type DeliveryServiceConfigDtoOutput,
	deliveryServiceConfigDtoSchema,
	LINE_ITEM_CODES,
	LogSeverity,
	OPERATIONS_SECTIONS,
	PricingModel,
	Services,
	type ServicesProvider,
	SETTINGS_GROUPS,
} from '@ekhodealer/ekho-common';
import { Form } from '@ekhodealer/ekho-common/rev';

import { useCurrentServicesProvider } from '@common/hooks/useCurrentServicesProvider';

import { useUpdateDeliveryService } from '../../../api/configuration/services/useUpdateDeliveryService';
import { useServiceProgramInfo } from '../../../hooks/useServiceProgramInfo';
import { FormSection } from '../../config-mgmt/FormSection';

/**
 * Helper function to get initial form data from ServicesProvider
 */
function getDefaultValues(
	servicesProvider: ServicesProvider | undefined | null,
): DeliveryServiceConfigDtoInput {
	const deliveryConfig = servicesProvider?.config?.[Services.DELIVERY] as
		| DeliveryServiceConfig
		| undefined;

	if (!deliveryConfig?.pricing?.length) {
		return {
			supported: false,
			pricing: [
				{
					model: PricingModel.FLAT_FEE,
					modelDetails: {
						flatFee: 0,
					},
				},
			],
			averageDeliveryTime: 0,
			requiresAssemblyAfterDelivery: false,
		};
	}

	const firstPricing = deliveryConfig.pricing[0];
	return {
		supported: deliveryConfig.supported ?? false,
		pricing: [firstPricing],
		averageDeliveryTime: deliveryConfig.averageDeliveryTime ?? 0,
		requiresAssemblyAfterDelivery: deliveryConfig.requiresAssemblyAfterDelivery ?? false,
	};
}

export function DeliveryServiceForm() {
	const { data: servicesProvider } = useCurrentServicesProvider();
	const programInfo = useServiceProgramInfo(OPERATIONS_SECTIONS.DELIVERY);
	const updateDeliveryServiceMutation = useUpdateDeliveryService();

	// Early return if service is not part of the program
	if (!programInfo.shouldRender) {
		return null;
	}

	// Handle form submit
	const handleSubmit = async (
		data: DeliveryServiceConfigDtoOutput,
	): Promise<{ isSuccess: boolean }> => {
		if (!servicesProvider) return { isSuccess: false };

		// Transform DTO to API payload format
		const dataToSubmit = {
			supported: data.supported,
			pricing: [
				{
					model: data.pricing[0].model,
					code: LINE_ITEM_CODES.SHIPPING_FEE,
					feeDescription: 'Delivery fee',
					modelDetails: data.pricing[0].modelDetails,
				},
			],
			averageDeliveryTime: data.averageDeliveryTime,
			requiresAssemblyAfterDelivery: data.requiresAssemblyAfterDelivery,
		};

		try {
			await updateDeliveryServiceMutation.mutateAsync(dataToSubmit);
			return { isSuccess: true };
		} catch (error) {
			createLog(
				'config-mgmt',
				`Failed to update delivery service configuration: ${
					error instanceof Error ? error.message : String(error)
				}`,
				LogSeverity.ERROR,
			);
			return { isSuccess: false };
		}
	};

	return (
		<FormSection
			sectionPath={{
				group: SETTINGS_GROUPS.OPERATIONS,
				section: OPERATIONS_SECTIONS.DELIVERY,
			}}
			form={{
				schema: deliveryServiceConfigDtoSchema,
				defaultValues: getDefaultValues(servicesProvider),
				onSubmit: handleSubmit,
			}}
			title='Delivery'
			description="Set the default delivery fee charged to buyers at checkout. Distance-based pricing models calculate distance between your inventory location and the buyer's delivery address."
			formTitle='Delivery pricing'
			toggleSettings={{
				fieldName: 'supported',
				visibility: 'write',
			}}
			hideDivider>
			<Form.Field.Pricing
				schemaField='pricing'
				supportedModels={[
					PricingModel.FLAT_FEE,
					PricingModel.LINEAR,
					PricingModel.STEPPED,
					PricingModel.STATE_BASED_FEE,
				]}
				modelLabelText='Delivery Fee'
				modelDescriptionText='Fee charged to the buyer at checkout for delivery. This can be customized per vehicle.'
			/>
		</FormSection>
	);
}
